import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useLocale from '../hooks/useLocale';

/**
 * Language picker (Rule 56).
 *
 * Two shapes for two places: `topbar` is a compact pill + dropdown that sits
 * beside the theme toggle, `inline` is a segmented control for settings panels
 * and the login page. Both read the same registry, so adding a language never
 * means touching this file.
 *
 * It is mounted pre-auth on the login screen deliberately — a member who reads
 * Filipino should be able to switch before they are asked to type a password,
 * not after.
 */
export default function LanguageSwitch({ variant = 'topbar' }) {
  const { t } = useTranslation();
  const { locale, setLocale, locales } = useLocale();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const active = locales.find((l) => l.code === locale) ?? locales[0];

  if (variant === 'inline') {
    return (
      <div className="lang-inline" role="group" aria-label={t('shell.language')}>
        {locales.map((l) => (
          <button
            key={l.code}
            type="button"
            className={`lang-inline-btn ${l.code === locale ? 'is-active' : ''}`}
            aria-pressed={l.code === locale}
            onClick={() => setLocale(l.code)}
          >
            {l.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="lang" ref={wrapRef}>
      <button
        type="button"
        className="shell-btn lang-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('shell.language')}
        title={t('shell.language')}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="lang-code">{active.code.toUpperCase()}</span>
      </button>

      {open && (
        <div className="lang-menu" role="menu" aria-label={t('shell.language')}>
          {locales.map((l) => (
            <button
              key={l.code}
              type="button"
              role="menuitemradio"
              aria-checked={l.code === locale}
              className={`lang-menu-item ${l.code === locale ? 'is-active' : ''}`}
              onClick={() => { setLocale(l.code); setOpen(false); }}
            >
              <span>{l.label}</span>
              <span className="lang-menu-code">{l.code.toUpperCase()}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
